import { roundOff, quantityRoundOffString } from "./NumberFormatHelper";
import * as SalesHelper from "helpers/SalesHelper";
import * as ProductHelper from "helpers/ProductHelper";
import AppBridgeHelper from "helpers/AppBridgeHelper";
import toast from "react-hot-toast";
import { STORAGE_KEYS, COLLECTIONS, PRINTER_COMMANDS, PRINTER_TYPES, APP_VERSION, CASHBOOK_ENTRY_TYPE } from "const";
import * as ShiftHelper from "helpers/ShiftHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import * as InvoiceHelper from "helpers/InvoiceHelper";
import { textToBytes, mergeBytes, padToTwoSides, bytesToBase64 } from "helpers/StringHelper";
import DateTimeHelpers from "./DateTimeHelpers";
import { db } from "./DBHelper";
import ShopsHelper from "./ShopsHelper";
import { textToEscPosBytes, getLogoEscPosBytes } from "helpers/ImageHelper";
import { PrinterManager } from "managers/PrinterManager";

const lineWidth = 48;

const toNumberSafe = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function normalizeSaleSummary(sale) {
  if (!sale) return {};

  const isAPIData = !!sale.date && !!sale.time;

  if (isAPIData) {
    return {
      id: sale.id,
      bill_no: sale.bill_no || "-",
      date: sale.date || "-",
      time: sale.time || "-",
      customer_name: sale.customer_name || "-",
      customer_mobile: sale.customer_mobile || "-",
      customer_tax_id: sale.customer_tax_id || "-",
      discount_applied_on_items: sale.discount_applied_on_items || 0,
      discount: sale.discount || 0,
      tax_amount: sale.tax_amount || 0,
      cess_amount: sale.cess_amount || 0,
      payable: sale?.payable || 0,
    };
  }

  return {
    id: sale.id,
    bill_no: sale.bill_no || "-",
    date: sale.time ? sale.time.split("T")[0] : "",
    time: sale.time ? sale.time.split("T")[1] : "",
    time_iso: sale.time || "",
    customer_name: sale.customer?.name || sale.name || "-",
    customer_mobile: sale.customer?.mobile || sale.mobile || "-",
    customer_tax_id: sale.customer?.tax_id || sale.trn_number || "-",
    discount_applied_on_items: sale.discount_applied_on_items || 0,
    discount: sale.discount || 0,
    tax_amount: sale.tax || 0,
    cess_amount: sale.cess || 0,
    payable: sale.payable || 0,
  };
}

export function itemsRowFromInvoices(invoices = []) {
  const map = new Map();

  invoices.forEach((invoice) => {
    const items = invoice.items || [];

    items.forEach((it) => {
      const itemId = it.id;

      const optionLabel = it.option?.label ?? "";
      const key = `${itemId}__${optionLabel}`;

      const qty = toNumberSafe(it.qty ?? it.count ?? it.sold_quantity ?? it.quantity ?? 0);

      const itemGrossAmount = Number(it.payable || 0) + Number(it.discount || 0);
      const grossUnitPrice = qty > 0 ? itemGrossAmount / qty : toNumberSafe(it.rateAndTax ?? it.rate ?? it.price) || 0;

      const existing = map.get(key);

      if (!existing) {
        map.set(key, {
          plu: it.item?.plu ?? it.plu ?? null,
          name: `${it.name ?? it.item?.name ?? it.item ?? ""}${it.option?.label ? ` - ${it.option.label}` : ""}`,
          hsn: it.item?.hsn ?? it.hsn ?? "",
          unit: it.unit ?? it.item?.unit ?? "",
          taxPercentage: it.taxPercentage,
          cessPercentage: it.cessPercentage,
          item_id: it.id ?? it.item_id ?? null,
          price: grossUnitPrice,
          priceSum: itemGrossAmount,
          sold_quantity: qty,
          payable: itemGrossAmount,
          tax: it.tax,
          cess: it.cess,
        });
      } else {
        existing.priceSum += itemGrossAmount;
        existing.sold_quantity += qty;
        existing.payable += itemGrossAmount;
        existing.tax += it.tax;
        existing.cess += it.cess;

        if (!existing.price && grossUnitPrice) existing.price = grossUnitPrice;
      }
    });
  });

  const rows = Array.from(map.values()).map((r) => ({
    ...r,
    price: r.sold_quantity > 0 ? Number((r.priceSum / r.sold_quantity).toFixed(2)) : r.price,
    sold_quantity: Number(r.sold_quantity?.toFixed?.(2) ?? r.sold_quantity),
    payable: Number(r.payable?.toFixed?.(2) ?? r.payable),
    tax: Number(r.tax?.toFixed?.(2) ?? r.tax_amount),
    cess: Number(r.cess?.toFixed?.(2) ?? r.cess_amount),
  }));

  return rows;
}

function processInvoiceCategorySummaries(invoices, getCategoryInfoForItem, getOrCreateSummary) {
  (invoices || []).forEach((invoice) => {
    (invoice.items || []).forEach((it) => {
      const { categoryId, categoryName } = getCategoryInfoForItem(it);

      const qty = toNumberSafe(it.qty ?? it.count ?? it.sold_quantity ?? it.quantity ?? 0);

      const priceCandidate = toNumberSafe(
        it.rate ?? it.price ?? (it.totalBeforeTax ? it.totalBeforeTax / (qty || 1) : undefined),
      );

      const itemPayable =
        it.payable != null && !isNaN(Number(it.payable))
          ? Number(it.payable)
          : (priceCandidate || 0) * qty - (it.discount || 0) + (it.tax || 0);

      const summary = getOrCreateSummary(categoryId, categoryName);

      summary.total += itemPayable;
      summary.salesQuantity += qty;
    });
  });
}

function processPurchaseCategorySummaries(purchases, getCategoryInfoForItem, getOrCreateSummary) {
  (purchases || []).forEach((purchase) => {
    (purchase.items || []).forEach((it) => {
      const { categoryId, categoryName } = getCategoryInfoForItem(it);

      const qty = toNumberSafe(it.quantity ?? it.qty ?? 1);
      const unitCost = toNumberSafe(it.cost ?? it.item?.cost ?? 0);

      getOrCreateSummary(categoryId, categoryName).purchaseAmount += unitCost * qty;
    });
  });
}

function processDamageCategorySummaries(damages, getCategoryInfoForItem, getOrCreateSummary) {
  (damages || []).forEach((it) => {
    const { categoryId, categoryName } = getCategoryInfoForItem(it);

    const qty = toNumberSafe(it.qty ?? it.quantity ?? 1);
    const price = toNumberSafe(it.price ?? it.rate ?? 0);

    const itemPayable =
      it.payable != null && !isNaN(Number(it.payable))
        ? Number(it.payable)
        : it.total != null && !isNaN(Number(it.total))
          ? Number(it.total)
          : price * qty;

    getOrCreateSummary(categoryId, categoryName).damageAmount += itemPayable;
  });
}

function generateCategorySummaries(invoices, purchases, damages, getCategoryInfoForItem, summaryMap) {
  const getOrCreateSummary = (categoryId, categoryName) => {
    const key = categoryId != null ? String(categoryId) : null;

    let existing = summaryMap.get(key);

    if (!existing) {
      existing = {
        id: categoryId ?? key,
        name: categoryName,
        total: 0,
        salesQuantity: 0,
        purchaseAmount: 0,
        damageAmount: 0,
      };

      summaryMap.set(key, existing);
    }

    return existing;
  };

  processInvoiceCategorySummaries(invoices, getCategoryInfoForItem, getOrCreateSummary);

  processPurchaseCategorySummaries(purchases, getCategoryInfoForItem, getOrCreateSummary);

  processDamageCategorySummaries(damages, getCategoryInfoForItem, getOrCreateSummary);
}

export async function categoryRowsFromInvoices(invoices = [], purchasesParam = null, damagesParam = null) {
  const [allCategories, fetchedPurchases, fetchedDamages] = await Promise.all([
    ProductHelper.getCategoriesFromDB().catch(() => []),
    purchasesParam === null
      ? db.getAll(COLLECTIONS.PURCHASES.name).catch(() => [])
      : Promise.resolve(purchasesParam || []),
    damagesParam === null
      ? db.getAll(COLLECTIONS.STOCK_DAMAGES.name).catch(() => [])
      : Promise.resolve(damagesParam || []),
  ]);

  const purchases = purchasesParam !== null ? purchasesParam || [] : fetchedPurchases;
  const damages = damagesParam !== null ? damagesParam || [] : fetchedDamages;

  const categoryMap = new Map();
  (allCategories || []).forEach((cat) => {
    if (cat && cat.id != null) {
      categoryMap.set(String(cat.id), cat.name);
    }
  });

  const getCategoryInfoForItem = (it) => {
    const rawCat = it.category ?? it.item?.category;
    const categoryId = typeof rawCat === "object" ? rawCat?.id : rawCat;
    const categoryName =
      (typeof rawCat === "object" ? rawCat?.name : null) ||
      (categoryId != null ? categoryMap.get(String(categoryId)) : null);

    return { categoryId, categoryName };
  };

  const summaryMap = new Map();
  generateCategorySummaries(invoices, purchases, damages, getCategoryInfoForItem, summaryMap);

  return Array.from(summaryMap.values()).map((row) => ({
    ...row,
    total: Number((row.total || 0).toFixed(2)),
    purchaseAmount: Number((row.purchaseAmount || 0).toFixed(2)),
    damageAmount: Number((row.damageAmount || 0).toFixed(2)),
    salesQuantity: Number((row.salesQuantity || 0).toFixed(2)),
  }));
}

export function itemisedRowsFromInvoices(invoices = [], opts = {}) {
  const rows = [];

  invoices.forEach((inv) => {
    if (opts.excludeVoided && String(inv.status).toLowerCase() === "void") return;

    const invoiceDate = inv.time ? String(inv.time).split("T")[0] : inv.date || "";
    let invoiceTime = "";
    if (inv.time) invoiceTime = String(inv.time).split("T")[1] || "";
    if (invoiceTime) invoiceTime = invoiceTime.replace(/Z$/, "");

    const billNo = inv.bill_no ?? "-";

    const items = Array.isArray(inv.items) ? inv.items : [];

    items.forEach((it, idx) => {
      const qty = toNumberSafe(it.qty ?? it.count ?? it.quantity ?? it.sold_quantity ?? it.qtySold ?? 0);

      const itemGrossAmount = Number(it.payable || 0) + Number(it.discount || 0);
      const price = qty > 0 ? itemGrossAmount / qty : toNumberSafe(it.rateAndTax ?? it.rate ?? it.price) || 0;

      const payable = itemGrossAmount;

      const taxPercent = toNumberSafe(it.taxPercentage ?? it.tax_percent ?? inv.tax ?? 0);
      const taxAmount = toNumberSafe(it.tax ?? it.taxAmount ?? (price * qty * taxPercent) / 100);

      const cessPercent = toNumberSafe(it.cessPercentage ?? it.cess_percent ?? inv.cess ?? 0);
      const cessAmount = toNumberSafe(it.cess ?? it.cessAmount ?? (price * qty * cessPercent) / 100);

      const row = {
        date: invoiceDate,
        time: invoiceTime,
        payable: roundOff(payable),
        bill_no: billNo,
        name: `${it.name ?? it.item ?? ""}${it.option?.label ? ` - ${it.option.label}` : ""}`,
        plu: it.plu ?? it.id ?? it.item_id ?? null,
        quantity: roundOff(qty),
        price: roundOff(price),
        cess_amount: roundOff(cessAmount),
        taxPercentage: roundOff(taxPercent),
        cessPercentage: roundOff(cessPercent),
        unit: it.unit ?? it.uom ?? inv.unit ?? "",
        tax_amount: roundOff(taxAmount),
        bill_no_suffix: inv.bare_bill_no ?? inv.bill_no_suffix ?? "",
        initiated_terminal_id: inv.initiated_terminal_id ?? inv.terminal_id ?? inv.initiated_terminal ?? null,
        cashier_id: inv.cashier_id ?? null,
        id: `${billNo || "bill"}_${String(it.plu ?? it.id ?? idx)}`,
        __rawInvoiceId: inv.id ?? inv.local_id,
        __rawItem: it,
      };

      rows.push(row);
    });
  });

  return rows;
}

export const getShiftFromLocalStorage = (shiftId) => {
  const stored = localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS);
  if (!stored) return null;
  const shifts = JSON.parse(stored);
  return shifts.find((s) => String(s.id) === String(shiftId)) || null;
};

const getSalesSummary = (rows) => {
  let totalAmount = 0;
  let totalAmountWithTax = 0;
  let totalDiscount = 0;
  let totalCredits = 0;
  let totalRoundOffAmount = 0;
  let totalTax = 0;

  rows.forEach((row) => {
    totalAmountWithTax += Number(row?.payable || 0);
    totalDiscount += Number(row?.discount_applied_on_items || 0) + Number(row?.discount || 0);
    totalCredits += Number(row?.customer_credit_used || 0);
    totalRoundOffAmount += Number(row?.roundOff || 0);
    totalTax += Number((row?.cess || 0) + (row?.tax || 0));
    totalAmount += Number(row?.total || 0);
  });

  return {
    totalAmount,
    totalAmountWithTax,
    totalDiscount,
    totalCredits,
    totalRoundOffAmount,
    totalTax,
  };
};

const getItemSummary = (rows) => {
  let totalAmount = 0;
  (rows || []).forEach((row) => {
    totalAmount += Number(row?.payable || 0);
  });

  return {
    totalAmount,
    grossTotal: rows?.summary?.grossTotal ?? totalAmount,
    totalDiscount: rows?.summary?.totalDiscount ?? 0,
    netTotal: rows?.summary?.netTotal ?? totalAmount,
  };
};

const getEditedSalesTotals = async (shiftId) => {
  let count = 0;
  let totalAmount = 0;
  const targetShiftId = shiftId || localStorage.getItem(STORAGE_KEYS.SHIFT_ID);
  if (targetShiftId) {
    try {
      const allEdited = await db.getAll(COLLECTIONS.EDITED_SALES.name);
      const shiftEdited = allEdited.filter((r) => String(r.shiftId) === String(targetShiftId));
      count = shiftEdited.length;
      totalAmount = shiftEdited.reduce((sum, r) => sum + Number(r.newAmount || 0), 0);
    } catch (err) {
      console.error("Failed to fetch edited sales totals:", err);
    }
  }
  return { count, totalAmount };
};

export function buildShopHeader(lines = []) {
  const invoiceFormat = InvoiceHelper.getInvoiceFormat();
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  if (invoiceFormat?.shop_name) {
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
    lines.push(textToBytes(`${invoiceFormat.shop_name}\n`));
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  }
  if (invoiceFormat?.address_1) {
    lines.push(textToBytes(`${invoiceFormat.address_1}\n`));
  }
  if (invoiceFormat?.address_2) {
    lines.push(textToBytes(`${invoiceFormat.address_2}\n`));
  }
  if (invoiceFormat?.address_3) {
    lines.push(textToBytes(`${invoiceFormat.address_3}\n`));
  }
  if (invoiceFormat?.address_4) {
    lines.push(textToBytes(`${invoiceFormat.address_4}\n`));
  }
  if (invoiceFormat?.tel_no) {
    lines.push(textToBytes(`Phone: +${invoiceFormat.tel_no}\n`));
  }
  if (invoiceFormat?.tin) {
    lines.push(textToBytes(`${invoiceFormat.tin}\n`));
  }
  lines.push(textToBytes("\n"));
}

const buildShiftDetails = function (lines, shift) {
  lines.push(textToBytes(padToTwoSides("Generated On:", `${DateTimeHelpers.formatDateTime(new Date()) || "-"}`)));
  lines.push(textToBytes(padToTwoSides("Shift:", `${shift?.name || "-"}`)));
  lines.push(textToBytes(padToTwoSides("CLOUD ID:", `${shift?.id || "-"}`)));
  lines.push(textToBytes(padToTwoSides("Opened At:", `${DateTimeHelpers.formatDateTime(shift?.opened_at) || "-"}`)));
  lines.push(
    textToBytes(
      padToTwoSides("Closed At:", `${shift?.closed_at ? DateTimeHelpers.formatDateTime(shift?.closed_at) : "-"}`),
    ),
  );
  lines.push(textToBytes(padToTwoSides("Status:", `${shift?.closed_at ? "CLOSED" : "OPEN"}`)));
};

const buildShiftDetailsMulti = async (addLine, shift) => {
  const padTwo = (l, r) => [
    { text: l, width: "50%", align: "left" },
    { text: r, width: "50%", align: "right" },
  ];

  await addLine(padTwo(`Generated On:`, `${DateTimeHelpers.formatDateTime(new Date()) || "-"}`));
  await addLine(padTwo(`Shift:`, `${shift?.name || "-"}`));
  await addLine(padTwo(`CLOUD ID:`, `${shift?.id || "-"}`));
  await addLine(padTwo(`Opened At:`, `${DateTimeHelpers.formatDateTime(shift?.opened_at) || "-"}`));
  await addLine(padTwo(`Closed At:`, `${shift?.closed_at ? DateTimeHelpers.formatDateTime(shift?.closed_at) : "-"}`));

  const statusV = shift?.closed_at ? "CLOSED" : "OPEN";
  await addLine(padTwo(`Status:`, statusV));
};

async function getShiftSalesSummaryData(shiftId) {
  let shiftOverview = {};
  let expenseTxns = [];
  if (shiftId) {
    try {
      const shiftData = await ShiftHelper.loadShiftHelper(shiftId);
      shiftOverview = shiftData?.shiftOverview || {};
      const allTxns = await db.getAll(COLLECTIONS.SHIFT_TRANSACTIONS.name);
      expenseTxns = allTxns.filter(
        (t) => String(t.shift_id) === String(shiftId) && t.type === CASHBOOK_ENTRY_TYPE.EXPENSE,
      );
    } catch (e) {
      console.error("Failed to load shift overview for sales summary:", e);
    }
  }
  return { shiftOverview, expenseTxns };
}

function appendSalesSummaryBasicTotals(lines, rows, shop, salesSummary) {
  lines.push(textToBytes(padToTwoSides("Number Of Invoices:", `${rows.length}`)));
  lines.push(
    textToBytes(padToTwoSides("Total Amount:", `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalAmount)}`)),
  );
  lines.push(
    textToBytes(padToTwoSides("Total Tax:", `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalTax)}`)),
  );
  lines.push(
    textToBytes(
      padToTwoSides(
        "Total Amount With Tax:",
        `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalAmountWithTax)}`,
      ),
    ),
  );
}

function appendSalesSummaryFinancialTotals(lines, shop, salesSummary, shiftOverview = {}) {
  const totalStockDamage = shiftOverview.totalStockDamage || 0;
  const totalPurchase = shiftOverview.totalPurchaseAmount || shiftOverview.totalPurchase || 0;
  const loyaltyPointSale = shiftOverview.totalLoyaltySales || 0;

  lines.push(
    textToBytes(
      padToTwoSides("Total Discount:", `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalDiscount)}`),
    ),
  );
  lines.push(
    textToBytes(padToTwoSides("Total Stock Damage:", `${ShopsHelper.getAmountFormatted(shop, totalStockDamage)}`)),
  );
  lines.push(textToBytes(padToTwoSides("Total Purchase:", `${ShopsHelper.getAmountFormatted(shop, totalPurchase)}`)));
  lines.push(
    textToBytes(
      padToTwoSides("Total Round Off:", `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalRoundOffAmount)}`),
    ),
  );
  lines.push(
    textToBytes(padToTwoSides("Loyalty Point Sale:", `${ShopsHelper.getAmountFormatted(shop, loyaltyPointSale)}`)),
  );
}

function appendSalesSummaryCreditDetails(lines, shop, shiftOverview = {}, expenseTxns = []) {
  lines.push(textToBytes("\nCREDIT DETAILS\n"));
  lines.push(
    textToBytes(
      padToTwoSides("Credit Sale:", `${ShopsHelper.getAmountFormatted(shop, shiftOverview.totalCredits || 0)}`),
    ),
  );
  lines.push(
    textToBytes(
      padToTwoSides(
        "Credit Received:",
        `${ShopsHelper.getAmountFormatted(shop, shiftOverview.totalCreditReceived || 0)}`,
      ),
    ),
  );
  lines.push(
    textToBytes(
      padToTwoSides(
        "Total Vendor Payment:",
        `${ShopsHelper.getAmountFormatted(shop, shiftOverview.totalVendorPayments || 0)}`,
      ),
    ),
  );

  if (expenseTxns.length > 0) {
    lines.push(textToBytes("-".repeat(lineWidth) + "\n"));
  }

  expenseTxns.forEach((expense) => {
    const typeName = expense.expenseType?.name;
    const cleanDescription = (expense.description || "").replace(/^EXPENSE:\s*/i, "").trim();
    const description = `${typeName} - ${cleanDescription}`;

    lines.push(
      textToBytes(
        padToTwoSides(description, `${ShopsHelper.getAmountFormatted(shop, expense.debit || expense.amount || 0)}`),
      ),
    );
  });

  lines.push(
    textToBytes(
      padToTwoSides("Total Expenses:", `${ShopsHelper.getAmountFormatted(shop, shiftOverview.totalExpenses || 0)}`),
    ),
  );
}

function appendSalesSummaryOverallSummary(lines, shop, shiftOverview = {}, deletedCarts = [], editedTotals) {
  const grossSales = shiftOverview.totalBillAmount ?? 0;
  const loyaltySale = shiftOverview.totalLoyaltySales || 0;
  const expenses = shiftOverview.totalExpenses || 0;
  const vendorPayment = shiftOverview.totalVendorPayments || 0;
  const creditSale = shiftOverview.totalCredits || 0;
  const creditReceived = shiftOverview.totalCreditReceived || 0;
  const openingBalance = shiftOverview.openingBalance || 0;
  const balanceInHand = shiftOverview.closingBalance ?? 0;

  lines.push(textToBytes("\nSUMMARY\n"));
  lines.push(textToBytes(padToTwoSides("Gross Sales:", `${ShopsHelper.getAmountFormatted(shop, grossSales)}`)));
  lines.push(
    textToBytes(padToTwoSides("Less: Loyalty Point Sale:", `${ShopsHelper.getAmountFormatted(shop, loyaltySale)}`)),
  );
  lines.push(textToBytes(padToTwoSides("Less: Expenses:", `${ShopsHelper.getAmountFormatted(shop, expenses)}`)));
  lines.push(
    textToBytes(padToTwoSides("Less: Vendor Payment:", `${ShopsHelper.getAmountFormatted(shop, vendorPayment)}`)),
  );
  lines.push(textToBytes(padToTwoSides("Less: Credit Sale:", `${ShopsHelper.getAmountFormatted(shop, creditSale)}`)));
  lines.push(
    textToBytes(padToTwoSides("Add: Credit Received:", `${ShopsHelper.getAmountFormatted(shop, creditReceived)}`)),
  );
  lines.push(
    textToBytes(padToTwoSides("Add: Opening Balance:", `${ShopsHelper.getAmountFormatted(shop, openingBalance)}`)),
  );
  lines.push(textToBytes(padToTwoSides("Balance In Hand:", `${ShopsHelper.getAmountFormatted(shop, balanceInHand)}`)));

  if (deletedCarts && deletedCarts.length > 0) {
    const deletedTotal = deletedCarts.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    lines.push(textToBytes(padToTwoSides("Deleted Carts:", `${ShopsHelper.getAmountFormatted(shop, deletedTotal)}`)));
  }

  if (editedTotals && editedTotals.count > 0) {
    lines.push(
      textToBytes(
        padToTwoSides("Edited Sales Value:", `${ShopsHelper.getAmountFormatted(shop, editedTotals.totalAmount)}`),
      ),
    );
  }
}

export async function getSalesSummaryReport(rows = [], shop = {}, shiftId, deletedCarts = []) {
  const shift = getShiftFromLocalStorage(shiftId);
  const salesSummary = getSalesSummary(rows);
  const editedTotals = await getEditedSalesTotals(shiftId);
  const { shiftOverview, expenseTxns } = await getShiftSalesSummaryData(shiftId);
  const lines = [];

  textToBytes(buildShopHeader(lines));

  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes(`SALES SUMMARY\n`));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);

  lines.push(textToBytes(padToTwoSides("Salesperson:", "All")));
  lines.push(textToBytes(padToTwoSides("From Date:", `${DateTimeHelpers.formatDateTime(shift?.opened_at) || "-"}`)));
  lines.push(textToBytes(padToTwoSides("To Date:", `${DateTimeHelpers.formatDateTime(new Date()) || "-"}`)));

  buildShiftDetails(lines, shift);
  appendSalesSummaryBasicTotals(lines, rows, shop, salesSummary);
  appendSalesSummaryFinancialTotals(lines, shop, salesSummary, shiftOverview);
  appendSalesSummaryCreditDetails(lines, shop, shiftOverview, expenseTxns);
  appendSalesSummaryOverallSummary(lines, shop, shiftOverview, deletedCarts, editedTotals);

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(textToBytes(`v${APP_VERSION}\n`));
  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  return mergeBytes(lines);
}

export async function getCustomerReportThreeInch(customer = {}, journals = [], shop = {}) {
  const lines = [];

  buildShopHeader(lines);

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes(`CUSTOMER STATEMENT\n`));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  lines.push(textToBytes("\n"));
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);

  lines.push(textToBytes(padToTwoSides("Name:", `${customer.name || "-"}`)));
  lines.push(textToBytes(padToTwoSides("Email:", `${customer.email || "-"}`)));
  lines.push(textToBytes(padToTwoSides("Mobile:", `${customer.mobile || "-"}`)));
  lines.push(textToBytes(padToTwoSides("Location:", `${customer.location || "-"}`)));
  lines.push(textToBytes(padToTwoSides("Balance:", `${ShopsHelper.getAmountFormatted(shop, customer.balance || 0)}`)));
  lines.push(
    textToBytes(padToTwoSides("Credit limit:", `${ShopsHelper.getAmountFormatted(shop, customer.credit_limit || 0)}`)),
  );
  lines.push(textToBytes(padToTwoSides("Points:", `${customer.points || 0}`)));
  lines.push(textToBytes(padToTwoSides("Loyalty card no:", `${customer.loyalty_card_no || "-"}`)));

  lines.push(textToBytes("=".repeat(lineWidth)));

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(textToBytes("TRANSACTION LEDGER\n"));
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
  lines.push(textToBytes("-".repeat(lineWidth)));

  journals.forEach((item) => {
    const dateTimeStr = DateTimeHelpers.formatDateTime(item.time || item.created_at) || "-";
    const desc = item.description || "-";
    const dr = ShopsHelper.getAmountFormatted(shop, item.debit || 0);
    const cr = ShopsHelper.getAmountFormatted(shop, item.credit || 0);
    const bal = ShopsHelper.getAmountFormatted(shop, item.balance || 0);

    lines.push(textToBytes(`Date: ${dateTimeStr}\n`));
    lines.push(textToBytes(`Desc: ${desc}\n`));
    lines.push(textToBytes(`Dr  : ${dr} | Cr: ${cr} | Bal: ${bal}\n`));
    lines.push(textToBytes("-".repeat(lineWidth)));
  });

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);

  return mergeBytes(lines);
}

function appendSalesReportRows(lines, rows, shop) {
  rows.forEach((row, index) => {
    const time = DateTimeHelpers.getLocalTime(row.time || row.time_iso) || "-";
    const invoiceNo = String(row.bill_no || "-");
    const amount = ShopsHelper.getAmountFormatted(shop, row.payable || 0);
    const tax = ShopsHelper.getAmountFormatted(shop, row?.cess + row?.tax || 0);

    let left = String(index + 1).padEnd(3);
    left += invoiceNo.padEnd(20);
    left += time.padEnd(8);
    left += String(tax).padStart(7);
    left += String(amount).padStart(10);

    lines.push(textToBytes(left));
  });
}

function buildReportExtraTotals(lines, deletedCarts, editedTotals, shop) {
  if (deletedCarts && deletedCarts.length > 0) {
    const deletedCount = deletedCarts.length;
    const deletedTotal = deletedCarts.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    lines.push(textToBytes(padToTwoSides("Total Carts Deleted:", `${deletedCount}`)));
    lines.push(
      textToBytes(padToTwoSides("Deleted Carts Value:", `${ShopsHelper.getAmountFormatted(shop, deletedTotal)}`)),
    );
  }

  if (editedTotals && editedTotals.count > 0) {
    lines.push(textToBytes(padToTwoSides("Total Sales Edited:", `${editedTotals.count}`)));
    lines.push(
      textToBytes(
        padToTwoSides("Edited Sales Value:", `${ShopsHelper.getAmountFormatted(shop, editedTotals.totalAmount)}`),
      ),
    );
  }
}

export async function getSalesReportThreeInch(rows = [], shop = {}, shiftId, deletedCarts = []) {
  const shift = getShiftFromLocalStorage(shiftId);
  const salesSummary = getSalesSummary(rows);
  const editedTotals = await getEditedSalesTotals(shiftId);
  const lines = [];

  textToBytes(buildShopHeader(lines));

  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes(`INVOICE REPORT\n`));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);

  lines.push(textToBytes(padToTwoSides("Salesperson:", "All")));
  lines.push(textToBytes(padToTwoSides("From Date:", `${DateTimeHelpers.formatDateTime(shift?.opened_at) || "-"}`)));
  lines.push(textToBytes(padToTwoSides("To Date:", `${DateTimeHelpers.formatDateTime(new Date()) || "-"}`)));

  buildShiftDetails(lines, shift);

  lines.push(textToBytes(padToTwoSides("Number Of Invoices:", `${rows.length}`)));
  lines.push(textToBytes("=".repeat(lineWidth)));

  lines.push(
    textToBytes("#".padEnd(3) + "Invoice No".padEnd(20) + "Time".padEnd(8) + "Tax".padStart(7) + "Amount".padStart(10)),
  );
  lines.push(textToBytes("-".repeat(lineWidth)));

  appendSalesReportRows(lines, rows, shop);

  lines.push(textToBytes("-".repeat(lineWidth)));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(
    textToBytes(
      padToTwoSides(
        "TOTAL",
        `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalAmountWithTax)}\n`,
        lineWidth / 2,
      ),
    ),
  );
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);

  buildReportExtraTotals(lines, deletedCarts, editedTotals, shop);

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  return mergeBytes(lines);
}

function appendItemSummaryTableRows(lines, rows, shop) {
  if (!rows.length) {
    lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  } else {
    lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
    rows.forEach((it) => {
      const plu = (it.plu || "-").toString().slice(0, 5).padEnd(5);
      const name = (it.name || "-").toString().slice(0, 16).padEnd(16);
      const rate = ShopsHelper.getAmountFormatted(shop, it.price || "0")
        .toString()
        .padStart(8);
      const tax = ShopsHelper.getAmountFormatted(shop, it.tax + it.cess || "0")
        .toString()
        .padStart(6);
      const qtyValue = isNaN(it.sold_quantity) ? "0" : String(it.sold_quantity);
      const qty = quantityRoundOffString(qtyValue).padStart(5);
      const amount = ShopsHelper.getAmountFormatted(shop, it.payable || "0")
        .toString()
        .padStart(8);

      lines.push(textToBytes(plu + name + qty + rate + tax + amount));
    });
  }
}

export async function getItemSummaryReport(rows = [], shop = {}, shiftId) {
  const shift = getShiftFromLocalStorage(shiftId);
  const itemSummary = getItemSummary(rows);
  const lines = [];

  textToBytes(buildShopHeader(lines));

  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes(`ITEMS REPORT\n`));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);

  lines.push(textToBytes(padToTwoSides("Salesperson:", "All")));
  lines.push(textToBytes(padToTwoSides("From Date:", `${DateTimeHelpers.formatDateTime(shift?.opened_at) || "-"}`)));
  lines.push(textToBytes(padToTwoSides("To Date:", `${DateTimeHelpers.formatDateTime(new Date()) || "-"}`)));

  buildShiftDetails(lines, shift);
  lines.push(textToBytes(padToTwoSides("Number Of Items Sold:", `${rows.length}`)));
  lines.push(textToBytes("=".repeat(lineWidth)));

  lines.push(
    textToBytes(
      "PLU".padEnd(5) +
        "Item".padEnd(16) +
        "Qty".padStart(5) +
        "Rate".padStart(8) +
        "Tax".padStart(6) +
        "Amount".padStart(8),
    ),
  );
  lines.push(textToBytes("-".repeat(lineWidth)));

  appendItemSummaryTableRows(lines, rows, shop);

  lines.push(textToBytes("-".repeat(lineWidth)));

  if (itemSummary.totalDiscount > 0) {
    lines.push(
      textToBytes(
        padToTwoSides("Item Total:", `${ShopsHelper.getAmountFormatted(shop, itemSummary.grossTotal)}`, lineWidth),
      ),
    );
    lines.push(
      textToBytes(
        padToTwoSides(
          "Total Discount:",
          `-${ShopsHelper.getAmountFormatted(shop, itemSummary.totalDiscount)}`,
          lineWidth,
        ),
      ),
    );
    lines.push(textToBytes("-".repeat(lineWidth)));
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
    lines.push(
      textToBytes(
        padToTwoSides("NET TOTAL", `${ShopsHelper.getAmountFormatted(shop, itemSummary.netTotal)}`, lineWidth / 2),
      ),
    );
  } else {
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
    lines.push(
      textToBytes(
        padToTwoSides("TOTAL", `${ShopsHelper.getAmountFormatted(shop, itemSummary.totalAmount)}`, lineWidth / 2),
      ),
    );
  }

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  return mergeBytes(lines);
}

export function getItemisedSummaryReport(rows = [], _shop = {}, shiftId) {
  const shift = getShiftFromLocalStorage(shiftId);

  const lineWidth = 48;
  const amount = (n) => roundOff(n || 0);

  const lines = [];
  textToBytes(buildShopHeader(lines));

  // Header
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes(`ITEMISED BILL SUMMARY\n`));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);

  lines.push(textToBytes(padToTwoSides("Salesperson:", "All")));
  lines.push(textToBytes(padToTwoSides("From Date:", `${DateTimeHelpers.formatDateTime(shift?.opened_at) || "-"}`)));
  lines.push(textToBytes(padToTwoSides("To Date:", `${DateTimeHelpers.formatDateTime(new Date()) || "-"}`)));

  buildShiftDetails(lines, shift);

  // Table header
  lines.push(textToBytes("=".repeat(lineWidth)));
  lines.push(
    textToBytes(
      "Invoice No".padEnd(14) + "PLU".padEnd(4) + "Name".padEnd(16) + "Qty".padStart(6) + "Total".padStart(8),
    ),
  );
  lines.push(textToBytes("-".repeat(lineWidth)));

  if (!rows.length) {
    lines.push(textToBytes("No Data"));
  } else {
    rows.forEach((it) => {
      const bill = (it.bill_no || it.fid || "-").toString().slice(0, 14).padEnd(14);
      const plu = (it.plu || "-").toString().slice(0, 4).padEnd(4);
      const name = (it.name || "-").toString().slice(0, 16).padEnd(16);

      const qtyValue = isNaN(it.quantity) ? "0" : String(it.quantity);
      const qty = qtyValue.padStart(6);

      const total = amount(it.payable).padStart(8);

      lines.push(textToBytes(bill + plu + name + qty + total));
    });
  }

  lines.push(textToBytes("-".repeat(lineWidth)));

  const totalDiscount = Number(rows?.summary?.totalDiscount || 0);
  const grossTotal = Number(rows?.summary?.grossTotal || rows.reduce((acc, it) => acc + Number(it.payable || 0), 0));
  const netTotal = Number(rows?.summary?.netTotal || grossTotal - totalDiscount);

  if (totalDiscount > 0) {
    lines.push(textToBytes(padToTwoSides("Item Total:", `${amount(grossTotal)}`, lineWidth)));
    lines.push(textToBytes(padToTwoSides("Total Discount:", `-${amount(totalDiscount)}`, lineWidth)));
    lines.push(textToBytes("-".repeat(lineWidth)));
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
    lines.push(textToBytes(padToTwoSides("NET TOTAL", `${amount(netTotal)}`, lineWidth / 2)));
  } else {
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
    lines.push(textToBytes(padToTwoSides("TOTAL", `${amount(grossTotal)}`, lineWidth / 2)));
  }

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  const finalBytes = mergeBytes(lines);
  return finalBytes;
}

export async function printReport({ reportText, fallbackFunction }) {
  if (!reportText) {
    toast.error("No data to print.");
    return false;
  }

  const bridge = AppBridgeHelper.getBridge();
  if (!bridge) {
    fallbackFunction?.();
    return false;
  }

  try {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    let { printerType, printerTarget } = TerminalHelper.getDefaultPrinterConfig(terminalSettings);

    if (printerType === PRINTER_TYPES.USB || printerType === PRINTER_TYPES.DIRECT_USB) {
      if (!printerTarget || printerTarget.toLowerCase() === "default") {
        printerTarget = null;
      }
    }

    const base64Text = bytesToBase64(reportText);
    await PrinterManager.print({ content: base64Text, printerType, printerTarget, label: `REPORT` });
    return true;
  } catch (err) {
    console.error("Print failed:", err);
    toast.error("Failed to print report.");
    return false;
  }
}

export async function getSalesForShift(shiftId) {
  if (!shiftId) return [];

  return await SalesHelper.getSalesByShift(shiftId);
}

export async function getSalesForSelectedShift() {
  const currentShiftId = localStorage.getItem(STORAGE_KEYS.SHIFT_ID);

  if (!currentShiftId) return [];

  return getSalesForShift(currentShiftId);
}

export async function getItemsSummaryForShift(shiftId) {
  try {
    const localSales = await getSalesForShift(shiftId);

    const aggregated = itemsRowFromInvoices(localSales);

    let totalDiscount = 0;
    let netTotal = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;

    (localSales || []).forEach((sale) => {
      const discountOnItems = Number(sale.discount_applied_on_items || 0);
      const billDiscount = Number(sale.discount || 0);
      totalDiscount += discountOnItems + billDiscount;
      netTotal += Number(sale.payable || 0);

      (sale.items || []).forEach((it) => {
        totalTax += Number(it.tax || 0);
        totalAdditionalTax += Number(it.cess || 0);
      });
    });

    const grossTotal = (aggregated || []).reduce((sum, r) => sum + Number(r.payable || 0), 0);

    aggregated.summary = {
      grossTotal,
      totalDiscount,
      netTotal,
      totalTax,
      totalAdditionalTax,
    };

    return aggregated;
  } catch (err) {
    console.error("Error in getItemsSummaryForShift:", err);
    return [];
  }
}

export async function getCategorySalesSummaryForShift(shiftId) {
  try {
    const targetShiftId = shiftId || localStorage.getItem(STORAGE_KEYS.SHIFT_ID);
    let localSales = [];
    if (targetShiftId) {
      localSales = await getSalesForShift(targetShiftId);
    } else {
      localSales = await SalesHelper.getSales();
    }
    const [purchases, damages] = await Promise.all([
      ShiftHelper.getPurchasesByShift(targetShiftId).catch(() => []),
      ShiftHelper.getStockDamagesByShift(targetShiftId).catch(() => []),
    ]);
    return await categoryRowsFromInvoices(localSales, purchases, damages);
  } catch (err) {
    console.error("Error in getCategorySalesSummaryForShift:", err);
    return [];
  }
}

export async function getItemisedRowsForShift(shiftId) {
  try {
    const localSales = await getSalesForShift(shiftId);

    const localRows = itemisedRowsFromInvoices(localSales);

    let totalDiscount = 0;
    let netTotal = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;

    (localSales || []).forEach((sale) => {
      const discountOnItems = Number(sale.discount_applied_on_items || 0);
      const billDiscount = Number(sale.discount || 0);
      totalDiscount += discountOnItems + billDiscount;
      netTotal += Number(sale.payable || 0);

      (sale.items || []).forEach((it) => {
        totalTax += Number(it.tax || 0);
        totalAdditionalTax += Number(it.cess || 0);
      });
    });

    const grossTotal = (localRows || []).reduce((sum, r) => sum + Number(r.payable || 0), 0);

    localRows.summary = {
      grossTotal,
      totalDiscount,
      netTotal,
      totalTax,
      totalAdditionalTax,
    };

    return localRows;
  } catch (err) {
    console.error("Error in getItemisedRowsForShift:", err);
    return [];
  }
}

export async function getSaleSummaryForShift({ shiftId } = {}) {
  try {
    const localSales = await getSalesForShift(shiftId);
    return localSales;
    // return localSales.map(normalizeSaleSummary);
  } catch (err) {
    console.error("Error in getSaleSummaryForShift:", err);
    return [];
  }
}

function appendShiftPaymentSummaryTable(lines, paymentModeSummaries = []) {
  lines.push(textToBytes("PAYMENT SUMMARY\n"));
  lines.push(textToBytes("=".repeat(lineWidth) + "\n"));
  const header =
    "MODE".padEnd(12) + "OPENING".padStart(9) + "IN".padStart(9) + "OUT".padStart(9) + "CLOSING".padStart(9) + "\n";
  lines.push(textToBytes(header));
  lines.push(textToBytes("-".repeat(lineWidth) + "\n"));

  let totalOpening = 0;
  let totalIn = 0;
  let totalOut = 0;
  let totalClosing = 0;

  paymentModeSummaries.forEach((pm) => {
    const opening = Number(pm.opening) || 0;
    const inVal = pm.transactions?.reduce((sum, t) => sum + (t.credit || 0), 0) ?? (Number(pm.in) || 0);
    const outVal = pm.transactions?.reduce((sum, t) => sum + (t.debit || 0), 0) ?? (Number(pm.out) || 0);
    const closing = Number(pm.closing) || 0;

    totalOpening += opening;
    totalIn += inVal;
    totalOut += outVal;
    totalClosing += closing;

    const modeStr = (pm.payment || "").toUpperCase().slice(0, 11).padEnd(12);
    const openStr = roundOff(opening).padStart(9);
    const inStr = roundOff(inVal).padStart(9);
    const outStr = roundOff(outVal).padStart(9);
    const closeStr = roundOff(closing).padStart(9);

    lines.push(textToBytes(`${modeStr}${openStr}${inStr}${outStr}${closeStr}\n`));
  });

  lines.push(textToBytes("-".repeat(lineWidth) + "\n"));
  const totMode = "TOTAL".padEnd(12);
  const totOpen = roundOff(totalOpening).padStart(9);
  const totIn = roundOff(totalIn).padStart(9);
  const totOut = roundOff(totalOut).padStart(9);
  const totClose = roundOff(totalClosing).padStart(9);
  lines.push(textToBytes(`${totMode}${totOpen}${totIn}${totOut}${totClose}\n`));
  lines.push(textToBytes("=".repeat(lineWidth) + "\n"));
}

function appendShiftFinancialTotals(lines, shiftOverview = {}) {
  lines.push(textToBytes(padToTwoSides("Total Expenses:", `${roundOff(shiftOverview.totalExpenses || 0)}`)));
  lines.push(textToBytes(padToTwoSides("Total Discount:", `${roundOff(shiftOverview.totalDiscount || 0)}`)));
  lines.push(textToBytes(padToTwoSides("Total Stock Damage:", `${roundOff(shiftOverview.totalStockDamage || 0)}`)));
  lines.push(
    textToBytes(
      padToTwoSides(
        "Total Purchase:",
        `${roundOff(shiftOverview.totalPurchaseAmount ?? shiftOverview.totalPurchase ?? 0)}`,
      ),
    ),
  );
  lines.push(
    textToBytes(
      padToTwoSides(
        "Total Round Off:",
        `${roundOff(shiftOverview.totalRoundOffAmount ?? shiftOverview.totalRoundOff ?? 0)}`,
      ),
    ),
  );
  lines.push(
    textToBytes(
      padToTwoSides(
        "Loyalty Point Sale:",
        `${roundOff(shiftOverview.totalLoyaltySales ?? shiftOverview.totalLoyaltySale ?? 0)}`,
      ),
    ),
  );
  lines.push(
    textToBytes(
      padToTwoSides(
        "Total Vendor Payment:",
        `${roundOff(shiftOverview.totalVendorPayments ?? shiftOverview.totalVendorPayment ?? 0)}`,
      ),
    ),
  );
  lines.push(
    textToBytes(
      padToTwoSides(
        "Total Credit Sale:",
        `${roundOff(shiftOverview.totalCredits ?? shiftOverview.totalCreditSale ?? 0)}`,
      ),
    ),
  );
  lines.push(textToBytes(padToTwoSides("Credit Received:", `${roundOff(shiftOverview.totalCreditReceived || 0)}`)));
  lines.push(textToBytes(padToTwoSides("Active Carts:", `${shiftOverview.activeCartsCount ?? 0}`)));
  lines.push(textToBytes(padToTwoSides("Active Carts Value:", `${roundOff(shiftOverview.activeCartsValue ?? 0)}`)));
}

function appendShiftClosingTable(lines, paymentModeSummaries = [], enteredBalances) {
  lines.push(textToBytes("\nSHIFT CLOSING\n"));
  lines.push(textToBytes("=".repeat(lineWidth) + "\n"));
  const header = "MODE".padEnd(15) + "EXPECTED".padStart(11) + "ACTUAL".padStart(11) + "DIFFERENCE".padStart(11) + "\n";
  lines.push(textToBytes(header));
  lines.push(textToBytes("-".repeat(lineWidth) + "\n"));

  let totalExpected = 0;
  let totalActual = 0;

  paymentModeSummaries.forEach((pm) => {
    const expected = Number(pm.closing) || 0;
    const enteredStr = enteredBalances ? enteredBalances[pm.id] : undefined;
    const actual =
      enteredStr !== undefined && enteredStr !== "" ? Number(enteredStr) : Number(pm.entered || pm.actual || 0);
    const diff = actual - expected;

    totalExpected += expected;
    totalActual += actual;

    const modeStr = (pm.payment || "").toUpperCase().slice(0, 14).padEnd(15);
    const expStr = roundOff(expected).padStart(11);
    const actStr = roundOff(actual).padStart(11);
    const diffStr = roundOff(diff).padStart(11);

    lines.push(textToBytes(`${modeStr}${expStr}${actStr}${diffStr}\n`));
  });

  lines.push(textToBytes("-".repeat(lineWidth) + "\n"));
  const totDiff = totalActual - totalExpected;
  const totMode = "TOTAL".padEnd(17);
  const totExp = roundOff(totalExpected).padStart(11);
  const totAct = roundOff(totalActual).padStart(9);
  const totDiffStr = roundOff(totDiff).padStart(11);
  lines.push(textToBytes(`${totMode}${totExp}${totAct}${totDiffStr}\n`));
  lines.push(textToBytes("=".repeat(lineWidth) + "\n"));
}

export async function getShiftReportPrintText({ shiftOverview = {}, paymentModeSummaries = [], enteredBalances }) {
  if (shiftOverview.activeCartsCount === undefined || shiftOverview.activeCartsValue === undefined) {
    try {
      const summary = await SalesHelper.getActiveCartsSummary(shiftOverview.shiftId);
      shiftOverview.activeCartsCount = summary.activeCartsCount;
      shiftOverview.activeCartsValue = summary.activeCartsValue;
    } catch (e) {
      console.error("Failed to load active carts summary for shift report:", e);
    }
  }

  const lines = [];
  buildShopHeader(lines);

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(textToBytes(`Shift Report For ${shiftOverview.name || ""}\n\n`));
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);

  lines.push(textToBytes(padToTwoSides("Operator:", `${shiftOverview.cashierName || "---"}`)));
  lines.push(textToBytes(padToTwoSides("Generated On:", `${DateTimeHelpers.formatDateTime(new Date()) || "-"}`)));
  lines.push(
    textToBytes(padToTwoSides("From Date:", `${DateTimeHelpers.formatDateTime(shiftOverview.opened) || "-"}`)),
  );
  lines.push(
    textToBytes(
      padToTwoSides(
        "To Date:",
        `${shiftOverview.closed ? DateTimeHelpers.formatDateTime(shiftOverview.closed) : DateTimeHelpers.formatDateTime(new Date())}`,
      ),
    ),
  );
  lines.push(textToBytes("\n"));

  appendShiftPaymentSummaryTable(lines, paymentModeSummaries);
  appendShiftFinancialTotals(lines, shiftOverview);
  appendShiftClosingTable(lines, paymentModeSummaries, enteredBalances);

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(textToBytes(`v${APP_VERSION}\n`));
  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  return mergeBytes(lines);
}

async function appendMultiThreeInchHeader(lines, addLine, shop, title) {
  const invoiceFormat = InvoiceHelper.getInvoiceFormat();
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);

  if (shop?.shop_logo) {
    const logoBytes = await getLogoEscPosBytes(shop.shop_logo);
    if (logoBytes != null) {
      lines.push(logoBytes);
      lines.push(textToBytes("\n"));
    }
  }

  if (invoiceFormat?.shop_name)
    await addLine(invoiceFormat.shop_name, { size: "double", weight: "bold", align: "center", lineHeight: 60 });
  if (invoiceFormat?.address_1) await addLine(invoiceFormat.address_1, { align: "center", lineHeight: 40 });
  if (invoiceFormat?.address_2) await addLine(invoiceFormat.address_2, { align: "center", lineHeight: 40 });
  if (invoiceFormat?.address_3) await addLine(invoiceFormat.address_3, { align: "center", lineHeight: 40 });
  if (invoiceFormat?.address_4) await addLine(invoiceFormat.address_4, { align: "center", lineHeight: 40 });
  if (invoiceFormat?.tel_no) await addLine(`Phone: +${invoiceFormat.tel_no}`, { align: "center", lineHeight: 40 });
  if (invoiceFormat?.tin) await addLine(invoiceFormat.tin, { align: "center", lineHeight: 40 });

  await addLine("\n");
  if (title) {
    await addLine(title, { size: "double", weight: "bold", align: "center" });
  }
}

async function appendSalesSummaryMultiFinancial(addLine, padToTwoSidesMulti, shop, salesSummary, shiftOverview = {}) {
  const totalStockDamage = shiftOverview.totalStockDamage || 0;
  const totalPurchase = shiftOverview.totalPurchaseAmount || shiftOverview.totalPurchase || 0;
  const loyaltyPointSale = shiftOverview.totalLoyaltySales || 0;

  await addLine(
    padToTwoSidesMulti(`Total Discount:`, `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalDiscount)}`),
  );
  await addLine(padToTwoSidesMulti(`Total Stock Damage:`, `${ShopsHelper.getAmountFormatted(shop, totalStockDamage)}`));
  await addLine(padToTwoSidesMulti(`Total Purchase:`, `${ShopsHelper.getAmountFormatted(shop, totalPurchase)}`));
  await addLine(
    padToTwoSidesMulti(`Total Round Off:`, `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalRoundOffAmount)}`),
  );
  await addLine(padToTwoSidesMulti(`Loyalty Point Sale:`, `${ShopsHelper.getAmountFormatted(shop, loyaltyPointSale)}`));
}

async function appendSalesSummaryMultiCreditDetails(
  addLine,
  padToTwoSidesMulti,
  shop,
  shiftOverview = {},
  expenseTxns = [],
) {
  await addLine(`\nCREDIT DETAILS\n`);
  await addLine(
    padToTwoSidesMulti(`Credit Sale:`, `${ShopsHelper.getAmountFormatted(shop, shiftOverview.totalCredits || 0)}`),
  );
  await addLine(
    padToTwoSidesMulti(
      `Credit Received:`,
      `${ShopsHelper.getAmountFormatted(shop, shiftOverview.totalCreditReceived || 0)}`,
    ),
  );
  await addLine(
    padToTwoSidesMulti(
      `Total Vendor Payment:`,
      `${ShopsHelper.getAmountFormatted(shop, shiftOverview.totalVendorPayments || 0)}`,
    ),
  );

  if (expenseTxns.length > 0) {
    await addLine("\n");
  }

  for (const expense of expenseTxns) {
    const typeName = expense.type?.name || (typeof expense.type === "string" ? expense.type : "");
    const cleanDesc = (expense.description || "").replace(/^EXPENSE:\s*/i, "").trim();
    const description = typeName && cleanDesc ? `${typeName} - ${cleanDesc}` : typeName || cleanDesc || "Expense";
    await addLine(padToTwoSidesMulti(description, `${ShopsHelper.getAmountFormatted(shop, expense.debit || 0)}`));
  }

  await addLine(
    padToTwoSidesMulti(`Total Expenses:`, `${ShopsHelper.getAmountFormatted(shop, shiftOverview.totalExpenses || 0)}`),
  );
}

async function appendSalesSummaryMultiSummarySection(
  addLine,
  padToTwoSidesMulti,
  shop,
  salesSummary,
  shiftOverview = {},
  deletedCarts = [],
  editedTotals,
) {
  const grossSales = shiftOverview.totalBillAmount ?? salesSummary.totalAmountWithTax ?? 0;
  const loyaltySale = shiftOverview.totalLoyaltySales || 0;
  const expenses = shiftOverview.totalExpenses || 0;
  const vendorPayment = shiftOverview.totalVendorPayments || 0;
  const creditSale = shiftOverview.totalCredits || salesSummary.totalCredits || 0;
  const creditReceived = shiftOverview.totalCreditReceived || 0;
  const openingBalance = shiftOverview.openingBalance || 0;
  const balanceInHand = shiftOverview.closingBalance ?? 0;

  await addLine(`\nSUMMARY`);
  await addLine(padToTwoSidesMulti(`Gross Sales:`, `${ShopsHelper.getAmountFormatted(shop, grossSales)}`));
  await addLine(
    padToTwoSidesMulti(`Less: Loyalty Point Sale:`, `${ShopsHelper.getAmountFormatted(shop, loyaltySale)}`),
  );
  await addLine(padToTwoSidesMulti(`Less: Expenses:`, `${ShopsHelper.getAmountFormatted(shop, expenses)}`));
  await addLine(padToTwoSidesMulti(`Less: Vendor Payment:`, `${ShopsHelper.getAmountFormatted(shop, vendorPayment)}`));
  await addLine(padToTwoSidesMulti(`Less: Credit Sale:`, `${ShopsHelper.getAmountFormatted(shop, creditSale)}`));
  await addLine(padToTwoSidesMulti(`Add: Credit Received:`, `${ShopsHelper.getAmountFormatted(shop, creditReceived)}`));
  await addLine(padToTwoSidesMulti(`Add: Opening Balance:`, `${ShopsHelper.getAmountFormatted(shop, openingBalance)}`));
  await addLine(padToTwoSidesMulti(`Balance In Hand:`, `${ShopsHelper.getAmountFormatted(shop, balanceInHand)}`));

  if (deletedCarts && deletedCarts.length > 0) {
    const deletedTotal = deletedCarts.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    await addLine(padToTwoSidesMulti(`Deleted Carts:`, `${ShopsHelper.getAmountFormatted(shop, deletedTotal)}`));
  }

  if (editedTotals && editedTotals.count > 0) {
    await addLine(
      padToTwoSidesMulti(`Edited Sales Value:`, `${ShopsHelper.getAmountFormatted(shop, editedTotals.totalAmount)}`),
    );
  }
}

async function appendSalesSummaryMultiBasicTotals(addLine, padToTwoSidesMulti, shop, rows, salesSummary) {
  await addLine(padToTwoSidesMulti(`Number Of Invoices:`, `${rows.length}`));
  await addLine(
    padToTwoSidesMulti(`Total Amount:`, `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalAmount)}`),
  );
  await addLine(padToTwoSidesMulti(`Total Tax:`, `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalTax)}`));
  await addLine(
    padToTwoSidesMulti(
      `Total Amount With Tax:`,
      `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalAmountWithTax)}`,
    ),
  );
}

export async function getSalesSummaryMultiThreeInch(rows = [], shop = {}, shiftId, deletedCarts = []) {
  const shift = getShiftFromLocalStorage(shiftId);
  const salesSummary = getSalesSummary(rows);
  const editedTotals = await getEditedSalesTotals(shiftId);
  const { shiftOverview, expenseTxns } = await getShiftSalesSummaryData(shiftId);
  const lines = [];

  const addLine = createReportAddLine(lines);
  const padToTwoSidesMulti = makePadToTwoSidesMulti("55%", "45%");

  await appendMultiThreeInchHeader(lines, addLine, shop, "SALES SUMMARY");
  await addLine(padToTwoSidesMulti(`Salesperson:`, "All"));
  await buildShiftDetailsMulti(addLine, shift);
  await addLine("-".repeat(lineWidth));

  await appendSalesSummaryMultiBasicTotals(addLine, padToTwoSidesMulti, shop, rows, salesSummary);
  await appendSalesSummaryMultiFinancial(addLine, padToTwoSidesMulti, shop, salesSummary, shiftOverview);
  await appendSalesSummaryMultiCreditDetails(addLine, padToTwoSidesMulti, shop, shiftOverview, expenseTxns);
  await appendSalesSummaryMultiSummarySection(
    addLine,
    padToTwoSidesMulti,
    shop,
    salesSummary,
    shiftOverview,
    deletedCarts,
    editedTotals,
  );

  await addLine(`v${APP_VERSION}`, { align: "center" });
  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  return mergeBytes(lines);
}

function createReportAddLine(lines) {
  return async (text, opts = {}) => {
    if (!text) return;
    const { size = "normal", weight = "normal", align = "left", lineHeight } = opts;
    const fontSize = size === "double" ? 32 : 24;
    const fontWeight = weight === "bold" ? "bold" : "normal";
    const bitmap = await textToEscPosBytes(text, { fontSize, fontWeight, align, lineHeight });
    if (bitmap) lines.push(bitmap);
  };
}

function makePadToTwoSidesMulti(leftWidth = "50%", rightWidth = "50%") {
  return (left, right) => [
    { text: left, width: leftWidth, align: "left" },
    { text: right, width: rightWidth, align: "right" },
  ];
}

async function appendMultiInvoiceReportRows(addLine, rows, shop) {
  const headers = [
    { text: "#", width: "8%", align: "left" },
    { text: "Invoice No", width: "38%", align: "left" },
    { text: "Time", width: "19%", align: "right" },
    { text: "Tax", width: "15%", align: "right" },
    { text: "Amount", width: "20%", align: "right" },
  ];
  await addLine(headers, { weight: "bold" });
  await addLine("-".repeat(lineWidth));

  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    const time = DateTimeHelpers.getLocalTime(row.time || row.time_iso) || "-";
    await addLine([
      { text: String(index + 1), width: "8%", align: "left" },
      { text: row.bill_no || "-", width: "38%", align: "left" },
      { text: time, width: "19%", align: "left" },
      { text: ShopsHelper.getAmountFormatted(shop, row?.cess + row?.tax || 0), width: "15%", align: "right" },
      { text: ShopsHelper.getAmountFormatted(shop, row.payable || 0), width: "20%", align: "right" },
    ]);
  }
}

export async function getSalesReportMultiThreeInch(rows = [], shop = {}, shiftId, _deletedCarts = []) {
  const shift = getShiftFromLocalStorage(shiftId);
  const salesSummary = getSalesSummary(rows);
  const lines = [];
  const addLine = createReportAddLine(lines);
  const padToTwoSidesMulti = makePadToTwoSidesMulti();

  await appendMultiThreeInchHeader(lines, addLine, shop, "INVOICE REPORT");
  await addLine(padToTwoSidesMulti(`Salesperson:`, "All"));
  await buildShiftDetailsMulti(addLine, shift);
  await addLine(padToTwoSidesMulti("Number Of Items Sold:", `${rows.length}`));
  await addLine("-".repeat(lineWidth));

  await appendMultiInvoiceReportRows(addLine, rows, shop);

  await addLine("-".repeat(lineWidth));
  await addLine(
    [
      { text: "TOTAL", width: "60%", align: "left" },
      {
        text: `${ShopsHelper.getAmountFormatted(shop, salesSummary.totalAmountWithTax)}`,
        width: "40%",
        align: "right",
      },
    ],
    { size: "double", weight: "bold" },
  );

  await addLine("-".repeat(lineWidth));
  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  return mergeBytes(lines);
}

async function appendItemSummaryRows(addLine, rows, shop) {
  const headers = [
    { text: "PLU", width: "10%", align: "left" },
    { text: "Item", width: "36%", align: "left" },
    { text: "Qty", width: "10%", align: "right" },
    { text: "Rate", width: "16%", align: "right" },
    { text: "Tax", width: "11%", align: "right" },
    { text: "Amount", width: "17%", align: "right" },
  ];
  await addLine(headers, { weight: "bold" });
  await addLine("-".repeat(lineWidth));

  for (const item of rows) {
    const plu = (item.item_id || "-").toString();
    const name = (item.name || "-").toString();
    const rate = ShopsHelper.getAmountFormatted(shop, item.price || "0");
    const tax = ShopsHelper.getAmountFormatted(shop, item.tax + item.cess || "0");
    const qtyValue = isNaN(item.sold_quantity) ? "0" : String(item.sold_quantity);
    const qty = quantityRoundOffString(qtyValue);
    const amount = ShopsHelper.getAmountFormatted(shop, item.payable || "0");

    await addLine([
      { text: plu, width: "10%", align: "left" },
      { text: name, width: "36%", align: "left" },
      { text: qty, width: "10%", align: "right" },
      { text: rate, width: "16%", align: "right" },
      { text: tax, width: "11%", align: "right" },
      { text: amount, width: "17%", align: "right" },
    ]);
  }
}

export async function getItemSummaryMultiThreeInch(rows = [], shop = {}, shiftId) {
  const shift = getShiftFromLocalStorage(shiftId);
  const itemSummary = getItemSummary(rows);
  const lines = [];
  const addLine = createReportAddLine(lines);
  const padToTwoSidesMulti = makePadToTwoSidesMulti();

  await appendMultiThreeInchHeader(lines, addLine, shop, "ITEMS REPORT");
  await addLine(padToTwoSidesMulti(`Salesperson:`, "All"));
  await buildShiftDetailsMulti(addLine, shift);
  await addLine(padToTwoSidesMulti("Number Of Items Sold:", `${rows.length}`));
  await addLine("=".repeat(lineWidth));

  await appendItemSummaryRows(addLine, rows, shop);

  await addLine("-".repeat(lineWidth));
  if (itemSummary.totalDiscount > 0) {
    await addLine([
      { text: "Item Total:", width: "60%", align: "left" },
      { text: `${ShopsHelper.getAmountFormatted(shop, itemSummary.grossTotal)}`, width: "40%", align: "right" },
    ]);
    await addLine([
      { text: "Total Discount:", width: "60%", align: "left" },
      { text: `-${ShopsHelper.getAmountFormatted(shop, itemSummary.totalDiscount)}`, width: "40%", align: "right" },
    ]);
    await addLine("-".repeat(lineWidth));
    await addLine(
      [
        { text: "NET TOTAL", width: "60%", align: "left" },
        { text: `${ShopsHelper.getAmountFormatted(shop, itemSummary.netTotal)}`, width: "40%", align: "right" },
      ],
      { size: "double", weight: "bold" },
    );
  } else {
    await addLine(
      [
        { text: "TOTAL", width: "60%", align: "left" },
        { text: `${ShopsHelper.getAmountFormatted(shop, itemSummary.totalAmount)}`, width: "40%", align: "right" },
      ],
      { size: "double", weight: "bold" },
    );
  }

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  return mergeBytes(lines);
}

export async function getItemisedSummaryMultiThreeInch(rows = [], shop = {}, shiftId) {
  const shift = getShiftFromLocalStorage(shiftId);
  const lines = [];

  const addLine = async (text, opts = {}) => {
    if (!text) return;
    const { size = "normal", weight = "normal", align = "left", lineHeight } = opts;
    const fontSize = size === "double" ? 32 : 24;
    const fontWeight = weight === "bold" ? "bold" : "normal";
    const bitmap = await textToEscPosBytes(text, { fontSize, fontWeight, align, lineHeight });
    if (bitmap) lines.push(bitmap);
  };

  const padToTwoSidesMulti = (left, right) => [
    { text: left, width: "50%", align: "left" },
    { text: right, width: "50%", align: "right" },
  ];

  await appendMultiThreeInchHeader(lines, addLine, shop, "ITEMISED SALES REPORT");
  await addLine(padToTwoSidesMulti(`Salesperson:`, "All"));
  await buildShiftDetailsMulti(addLine, shift);
  await addLine("=".repeat(lineWidth));

  const headers = [
    { text: "Inv No", width: "30%", align: "left" },
    { text: "PLU", width: "10%", align: "left" },
    { text: "Name", width: "35%", align: "left" },
    { text: "Qty", width: "7%", align: "right" },
    { text: "Total", width: "18%", align: "right" },
  ];

  await addLine(headers, { weight: "bold" });
  await addLine("-".repeat(lineWidth));

  for (const it of rows) {
    const bill = (it.bill_no || it.fid || "-").toString();
    const plu = (it.plu || "-").toString();
    const name = (it.name || "-").toString();
    const qtyValue = isNaN(it.quantity) ? "0" : String(it.quantity);
    const qty = quantityRoundOffString(qtyValue);
    const total = ShopsHelper.getAmountFormatted(shop, it.payable || "0");

    await addLine([
      { text: bill, width: "30%", align: "left" },
      { text: plu, width: "10%", align: "left" },
      { text: name, width: "35%", align: "left" },
      { text: qty, width: "7%", align: "right" },
      { text: total, width: "18%", align: "right" },
    ]);
  }

  await addLine("-".repeat(lineWidth));

  const totalDiscount = Number(rows?.summary?.totalDiscount || 0);
  const grossTotal = Number(rows?.summary?.grossTotal || rows.reduce((acc, it) => acc + Number(it.payable || 0), 0));
  const netTotal = Number(rows?.summary?.netTotal || grossTotal - totalDiscount);

  if (totalDiscount > 0) {
    await addLine([
      { text: "Item Total:", width: "60%", align: "left" },
      { text: `${ShopsHelper.getAmountFormatted(shop, grossTotal)}`, width: "40%", align: "right" },
    ]);
    await addLine([
      { text: "Total Discount (-):", width: "60%", align: "left" },
      { text: `${ShopsHelper.getAmountFormatted(shop, totalDiscount)}`, width: "40%", align: "right" },
    ]);
    await addLine("-".repeat(lineWidth));
    await addLine(
      [
        { text: "NET TOTAL", width: "60%", align: "left" },
        { text: `${ShopsHelper.getAmountFormatted(shop, netTotal)}`, width: "40%", align: "right" },
      ],
      { size: "double", weight: "bold" },
    );
  } else {
    await addLine(
      [
        { text: "TOTAL", width: "60%", align: "left" },
        { text: `${ShopsHelper.getAmountFormatted(shop, grossTotal)}`, width: "40%", align: "right" },
      ],
      { size: "double", weight: "bold" },
    );
  }

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  return mergeBytes(lines);
}

export async function getCategorySalesReport(rows = [], shop = {}, startDateStr, endDateStr) {
  const lineWidth = 48;
  const lines = [];

  textToBytes(buildShopHeader(lines));

  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes(`CATEGORY SALES REPORT\n`));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);

  if (startDateStr) lines.push(textToBytes(padToTwoSides("From Date:", `${startDateStr}`)));
  if (endDateStr) lines.push(textToBytes(padToTwoSides("To Date:", `${endDateStr}`)));

  lines.push(textToBytes("=".repeat(lineWidth)));

  // Table Header
  lines.push(
    textToBytes(
      "S.No".padEnd(4) + "Category Name".padEnd(18) + "Sales".padStart(9) + "Purch".padStart(8) + "Damg".padStart(9),
    ),
  );
  lines.push(textToBytes("-".repeat(lineWidth)));

  let totalSum = 0;
  let totalPurchaseSum = 0;
  let totalDamageSum = 0;
  if (!rows.length) {
    lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  } else {
    lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
    rows.forEach((it, index) => {
      const sNo = (it.sNo || index + 1).toString().slice(0, 4).padEnd(4);
      const name = (it.name || "-").toString().slice(0, 18).padEnd(18);
      const totalVal = Number(it.total) || 0;
      const purchVal = Number(it.purchaseAmount) || 0;
      const damgVal = Number(it.damageAmount) || 0;

      totalSum += totalVal;
      totalPurchaseSum += purchVal;
      totalDamageSum += damgVal;

      const amount = ShopsHelper.getAmountFormatted(shop, totalVal).toString().padStart(9);
      const purchStr = ShopsHelper.getAmountFormatted(shop, purchVal).toString().padStart(8);
      const damgStr = ShopsHelper.getAmountFormatted(shop, damgVal).toString().padStart(9);

      lines.push(textToBytes(sNo + name + amount + purchStr + damgStr));
    });
  }

  lines.push(textToBytes("-".repeat(lineWidth)));
  lines.push(textToBytes(padToTwoSides("TOTAL SALES:", `${ShopsHelper.getAmountFormatted(shop, totalSum)}`)));
  lines.push(
    textToBytes(padToTwoSides("TOTAL PURCHASES:", `${ShopsHelper.getAmountFormatted(shop, totalPurchaseSum)}`)),
  );
  lines.push(textToBytes(padToTwoSides("TOTAL DAMAGES:", `${ShopsHelper.getAmountFormatted(shop, totalDamageSum)}`)));

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  return mergeBytes(lines);
}

async function renderCategorySalesRowsMulti(rows, shop, addLine) {
  let totalSum = 0;
  let totalPurchaseSum = 0;
  let totalDamageSum = 0;
  for (let idx = 0; idx < rows.length; idx++) {
    const item = rows[idx];
    const sNo = (item.sNo || idx + 1).toString();
    const name = (item.name || "-").toString();
    const totalVal = Number(item.total) || 0;
    const purchVal = Number(item.purchaseAmount) || 0;
    const damgVal = Number(item.damageAmount) || 0;

    totalSum += totalVal;
    totalPurchaseSum += purchVal;
    totalDamageSum += damgVal;

    const amount = ShopsHelper.getAmountFormatted(shop, totalVal);
    const purchStr = ShopsHelper.getAmountFormatted(shop, purchVal);
    const damgStr = ShopsHelper.getAmountFormatted(shop, damgVal);

    await addLine([
      { text: sNo, width: "10%", align: "left" },
      { text: name, width: "36%", align: "left" },
      { text: amount, width: "18%", align: "right" },
      { text: purchStr, width: "18%", align: "right" },
      { text: damgStr, width: "18%", align: "right" },
    ]);
  }
  return { totalSum, totalPurchaseSum, totalDamageSum };
}

export async function getCategorySalesMultiThreeInch(rows = [], shop = {}, startDateStr, endDateStr) {
  const lines = [];

  const addLine = async (text, opts = {}) => {
    if (!text) return;
    const { size = "normal", weight = "normal", align = "left", lineHeight } = opts;
    const fontSize = size === "double" ? 32 : 24;
    const fontWeight = weight === "bold" ? "bold" : "normal";
    const bitmap = await textToEscPosBytes(text, { fontSize, fontWeight, align, lineHeight });
    if (bitmap) lines.push(bitmap);
  };

  const padToTwoSidesMulti = (left, right) => [
    { text: left, width: "50%", align: "left" },
    { text: right, width: "50%", align: "right" },
  ];

  await appendMultiThreeInchHeader(lines, addLine, shop, "CATEGORY SALES REPORT");

  if (startDateStr) await addLine(padToTwoSidesMulti("From Date:", `${startDateStr}`));
  if (endDateStr) await addLine(padToTwoSidesMulti("To Date:", `${endDateStr}`));

  await addLine("=".repeat(lineWidth));

  const headers = [
    { text: "S.No", width: "10%", align: "left" },
    { text: "Category Name", width: "36%", align: "left" },
    { text: "Sales", width: "18%", align: "right" },
    { text: "Purch", width: "18%", align: "right" },
    { text: "Damg", width: "18%", align: "right" },
  ];

  await addLine(headers, { weight: "bold" });
  await addLine("-".repeat(lineWidth));

  const { totalSum, totalPurchaseSum, totalDamageSum } = await renderCategorySalesRowsMulti(rows, shop, addLine);

  await addLine("-".repeat(lineWidth));
  await addLine(padToTwoSidesMulti("TOTAL SALES:", `${ShopsHelper.getAmountFormatted(shop, totalSum)}`));
  await addLine(padToTwoSidesMulti("TOTAL PURCHASES:", `${ShopsHelper.getAmountFormatted(shop, totalPurchaseSum)}`));
  await addLine(padToTwoSidesMulti("TOTAL DAMAGES:", `${ShopsHelper.getAmountFormatted(shop, totalDamageSum)}`));

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  return mergeBytes(lines);
}
